/**
 * Infinite scroll for Dawn's main-collection-product-grid section.
 *
 * How it works:
 * - Wraps #product-grid + the pagination markup inside <infinite-scroll-collection>.
 * - An IntersectionObserver watches the last product card in the grid.
 * - When it scrolls into view, we fetch the next page (?page=N&section_id=...),
 *   pull out the new #product-grid's children, and append them to the existing grid.
 * - Preserves the current URL's query string (filters/sort) when building the next
 *   page URL, so infinite scroll keeps working correctly with active filters.
 * - If a fetch fails, we restore Dawn's default pagination link as a fallback so
 *   the user is never stuck without a way to reach the next page.
 */
class InfiniteScrollCollection extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
  }

  connectedCallback() {
    this.grid = this.querySelector('#product-grid');
    this.paginationWrapper = this.querySelector('.infinite-scroll__pagination');
    this.loadingSpinner = this.querySelector('.infinite-scroll__loading');

    if (!this.grid) return;

    this.sectionId = this.dataset.sectionId;
    this.currentPage = parseInt(this.dataset.currentPage, 10) || 1;
    this.totalPages = parseInt(this.dataset.totalPages, 10) || 1;

    // Already on the last page — nothing to observe, let default (empty) pagination be.
    if (this.currentPage >= this.totalPages) return;

    // Progressive enhancement: hide Dawn's default pagination nav once JS is driving.
    const defaultNav = this.paginationWrapper ? this.paginationWrapper.querySelector('.pagination-wrapper') : null;
    if (defaultNav) defaultNav.style.display = 'none';

    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
      rootMargin: '600px 0px 0px 0px',
    });
    this.observeLastItem();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  observeLastItem() {
    if (!this.observer || !this.grid.lastElementChild) return;
    this.observer.observe(this.grid.lastElementChild);
  }

  handleIntersect(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting && !this.loading) {
        this.loadNextPage();
      }
    });
  }

  buildNextPageUrl() {
    const url = new URL(window.location.href);
    url.searchParams.set('page', this.currentPage + 1);
    url.searchParams.set('section_id', this.sectionId);
    return url.toString();
  }

  async loadNextPage() {
    this.loading = true;
    if (this.loadingSpinner) this.loadingSpinner.hidden = false;
    if (this.observer && this.grid.lastElementChild) {
      this.observer.unobserve(this.grid.lastElementChild);
    }

    try {
      const response = await fetch(this.buildNextPageUrl());
      if (!response.ok) throw new Error('Infinite scroll fetch failed: ' + response.status);

      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newGrid = doc.querySelector('#product-grid');

      if (newGrid && newGrid.children.length) {
        Array.from(newGrid.children).forEach((item) => {
          this.grid.appendChild(item);
        });
      }

      this.currentPage += 1;

      if (this.currentPage >= this.totalPages) {
        if (this.observer) this.observer.disconnect();
      } else {
        this.observeLastItem();
      }
    } catch (error) {
      console.error(error);
      // Fallback: restore default pagination link so the user isn't stuck.
      const defaultNav = this.paginationWrapper ? this.paginationWrapper.querySelector('.pagination-wrapper') : null;
      if (defaultNav) defaultNav.style.display = '';
      if (this.observer) this.observer.disconnect();
    } finally {
      this.loading = false;
      if (this.loadingSpinner) this.loadingSpinner.hidden = true;
    }
  }
}

if (!customElements.get('infinite-scroll-collection')) {
  customElements.define('infinite-scroll-collection', InfiniteScrollCollection);
}